import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { formatApiErrorDetail } from '../../lib/adminApi';
import { PORTAL_LOGIN } from '../../constants/testIds';

const PortalLogin = () => {
  const { login, signup } = usePortalAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin'); // signin | signup
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = `${window.location.origin}/portal/auth-callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signup(name, email, password);
      } else {
        await login(email, password);
      }
      navigate('/portal');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={PORTAL_LOGIN.page} className="min-h-screen bg-[#FFFBF5] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-10">
          <span className="w-12 h-12 rounded-xl bg-[#F97316] flex items-center justify-center shadow-sm">
            <MapPin size={24} className="text-white" strokeWidth={2.5} />
          </span>
          <span className="text-[28px] font-extrabold tracking-tight text-stone-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Ambanji
          </span>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-8">
          <h1 className="text-xl font-bold text-stone-900 mb-1 text-center">
            {mode === 'signup' ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className="text-sm text-stone-500 text-center mb-6">Client portal — track your heatmap and review posts.</p>

          <button
            data-testid={PORTAL_LOGIN.googleButton}
            type="button"
            onClick={handleGoogle}
            className="w-full py-3.5 rounded-xl border-2 border-stone-200 text-sm font-bold text-stone-800 hover:border-stone-400 transition flex items-center justify-center gap-3 mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.56 2.7-3.87 2.7-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.55-1.84.86-3.06.86-2.36 0-4.35-1.6-5.07-3.74H.96v2.35A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.93 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.16.29-1.68V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.03z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.6 8.6 0 0 0 9 0 9 9 0 0 0 .96 4.97l2.97 2.35C4.65 5.18 6.64 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs text-stone-400 font-medium">or</span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <input
                data-testid={PORTAL_LOGIN.nameInput}
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
              />
            )}
            <input
              data-testid={PORTAL_LOGIN.emailInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
            />
            <input
              data-testid={PORTAL_LOGIN.passwordInput}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
            />

            {error && (
              <p data-testid={PORTAL_LOGIN.errorMessage} className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            <button
              data-testid={PORTAL_LOGIN.submitButton}
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl bg-[#4F46E5] text-white text-sm font-bold hover:bg-[#4338CA] transition disabled:opacity-60"
            >
              {loading ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
            </button>
          </form>

          <button
            data-testid={PORTAL_LOGIN.toggleModeButton}
            type="button"
            onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(''); }}
            className="w-full text-center text-sm text-stone-500 mt-5 hover:text-stone-800 transition"
          >
            {mode === 'signup' ? 'Already have an account? Sign in' : "New here? Create an account"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
